import { useCallback, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { getNotes } from "../api";
import type {
  CustomerPace,
  MetricsPage,
  NoteKind,
  Notes,
  PeriodsPage,
  ReportDetail,
  SuggestionList,
  TrackedItem,
} from "../api";
import { HistoryDrawer } from "../notes/HistoryDrawer";
import { NoteCard } from "../notes/NoteCard";
import AskCard from "../overview/AskCard";
import Attention from "../overview/Attention";
import CampaignUsage from "../overview/CampaignUsage";
import KeyNumbers from "../overview/KeyNumbers";
import SinceReport from "../overview/SinceReport";
import { useFetch } from "../overview/useFetch";
import { useCustomer } from "./CustomerLayout";

/**
 * The customer's landing tab: what needs attention on the account, the
 * numbers behind it, and the notes the people covering it keep.
 *
 * Assembled entirely from endpoints other tabs already use -- it computes
 * nothing the Metrics, Revenue or Report tab could disagree with, and
 * every one of them reads Postgres, never Redshift (invariant 38). The
 * report's source material is deliberately not fetched (invariant 33).
 *
 * Mutations refetch rather than patch local state, as `ActionDetail` does:
 * the server assigns version numbers and authorship, so its answer is the
 * only one worth showing.
 */
export default function CustomerOverview() {
  const location = useLocation();
  // The report used to live at /c/:ref, so a link pasted before it moved
  // (`?r=<id>#thread-<uuid>`) still has to arrive at the report it meant.
  const legacy =
    new URLSearchParams(location.search).has("r") || location.hash.startsWith("#thread-");

  if (legacy) {
    return (
      <Navigate
        to={{ pathname: "report", search: location.search, hash: location.hash }}
        replace
      />
    );
  }
  return <Overview />;
}

function Overview() {
  const { customer, refresh } = useCustomer();
  const c = customer.name;

  const pace = useFetch<CustomerPace | null>(`/api/customers/${c}/revenue/pace`);
  const metrics = useFetch<MetricsPage>(`/api/customers/${c}/metrics`);
  const periods = useFetch<PeriodsPage>(`/api/customers/${c}/metrics/periods`);
  const report = useFetch<ReportDetail>(`/api/customers/${c}/reports/latest`);
  const suggestions = useFetch<SuggestionList>(`/api/customers/${c}/reports/latest/suggestions`);
  const actions = useFetch<{ items: TrackedItem[] }>(
    `/api/actions?customer=${encodeURIComponent(c)}`,
  );

  // Tracking a suggestion changes three things on this page and the count
  // on the tab bar; all four are re-read rather than patched.
  const onTracked = useCallback(() => {
    suggestions.reload();
    actions.reload();
    refresh();
  }, [suggestions.reload, actions.reload, refresh]);

  const platform = metrics.data?.has_platform_domains ? metrics.data : null;
  const untracked =
    report.data && suggestions.data !== undefined
      ? report.data.report.action_items.filter(
          (a) =>
            a.status === "open" &&
            a.id &&
            !suggestions.data?.suggestions.some((s) => s.action_id === a.id && s.tracked_id),
        ).length
      : undefined;

  const errors = [pace, metrics, periods, report, suggestions, actions]
    .map((f) => f.error)
    .filter((e): e is string => e !== null);
  const loadingAttention = report.data === undefined || metrics.data === undefined;

  return (
    <div className="ov">
      {errors.length > 0 && (
        <div className="error">Some of this page could not load: {[...new Set(errors)].join("; ")}</div>
      )}

      <KeyNumbers
        pace={pace.data}
        periods={platform ? periods.data : null}
        tracked={actions.data?.items}
        untracked={untracked}
      />

      <div className="ov-grid">
        <div className="ov-main">
          {loadingAttention && !report.error && !metrics.error ? (
            <div className="empty">Loading…</div>
          ) : (
            <Attention
              customer={c}
              signals={platform?.signals ?? []}
              report={report.data ?? null}
              suggestions={suggestions.data ?? null}
              onTracked={onTracked}
            />
          )}

          {report.data === null && (
            <p className="dim">No report has been written for this customer yet.</p>
          )}
          {report.data && <SinceReport customer={c} report={report.data} />}

          {platform && <CampaignUsage campaigns={platform.campaigns} pace={pace.data} />}
        </div>

        <aside className="ov-aside" aria-label="Customer context">
          {report.data && <AskCard name={customer.display_name} report={report.data} />}
          <Notebook customer={c} />
        </aside>
      </div>
    </div>
  );
}

/** The KPI and Updates notes, with their history drawer. */
function Notebook({ customer }: { customer: string }) {
  const [notes, setNotes] = useState<Notes | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<NoteKind | null>(null);

  const load = useCallback(() => {
    setError(null);
    void getNotes(customer)
      .then(setNotes)
      .catch((e) => setError(e.message));
  }, [customer]);

  useEffect(load, [load]);

  const closeHistory = useCallback(() => setHistory(null), []);

  return (
    <>
      {error && <div className="error">{error}</div>}
      {!notes ? (
        !error && <div className="empty">Loading notes…</div>
      ) : (
        <>
          {notes.notes.map((n) => (
            <NoteCard
              key={n.kind}
              customer={customer}
              note={n}
              canEdit={notes.can_edit}
              onChanged={load}
              onHistory={() => setHistory(n.kind)}
            />
          ))}
          {!notes.can_edit && (
            <p className="dim">Only people assigned to this company can edit these notes.</p>
          )}
        </>
      )}

      {history && notes && (
        <HistoryDrawer
          customer={customer}
          kind={history}
          canEdit={notes.can_edit}
          onClose={closeHistory}
          onRestored={load}
        />
      )}
    </>
  );
}
